import { useState } from 'react'
import styled from 'styled-components'
import { Flex } from './ui/Layout'
import { FormProvider } from './context/FormContext'
import './App.css'
import { FormConfigProvider, useFormConfig } from './context/FormConfigContext'
import { submitTestimonial } from './utils/submit'
import { Text } from './ui/Typography'
import CliparaSpinner from './ui/CliparaSpinner'

// Import pages
import Intro from './pages/Intro'
import Permissions from './pages/Permissions'
import Tips from './pages/Tips'
import VideoRecording from './pages/VideoRecording'
import Review from './pages/Review'
import ThankYou from './pages/ThankYou'

// Page enum
const PAGES = {
  INTRO: 'INTRO',
  PERMISSIONS: 'PERMISSIONS',
  TIPS: 'TIPS',
  VIDEO_RECORDING: 'VIDEO_RECORDING',
  REVIEW: 'REVIEW',
  THANK_YOU: 'THANK_YOU'
}

const AppContainer = styled(Flex)`
  width: 100%;
  min-height: 100vh;
`

const AppContent = () => {
  const [currentPage, setCurrentPage] = useState(PAGES.INTRO)
  const [recordedVideoBlob, setRecordedVideoBlob] = useState(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [uploadProgress, setUploadProgress] = useState(0)
  const [uploadError, setUploadError] = useState(null)
  const { formConfig, isLoading, error } = useFormConfig()

  const handleSubmit = async (values) => {
    console.log('handleSubmit running', values, recordedVideoBlob)
    setIsSubmitting(true)
    setUploadError(null)

    try {
      await submitTestimonial({
        values,
        recordedVideoBlob,
        setUploadProgress,
        setUploadError,
        formConfig
      })
      setCurrentPage(PAGES.THANK_YOU)
    } catch (error) {
      console.error('Error submitting form:', error)
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleVideoBlob = (blob) => {
    setRecordedVideoBlob(blob)
    setCurrentPage(PAGES.REVIEW)
  }

  const renderCurrentPage = () => {
    if (isLoading) {
      return (
        <Flex justifyContent="center" alignItems="center">
          <CliparaSpinner width="48px" height="48px" />
        </Flex>
      )
    }

    if (error) {
      return <Text color="error">{error}</Text>
    }

    if (!formConfig) {
      return <Text>No form configuration found</Text>
    }

    switch (currentPage) {
      case PAGES.INTRO:
        return <Intro onNext={() => setCurrentPage(PAGES.TIPS)} />
      case PAGES.TIPS:
        return <Tips onBack={() => setCurrentPage(PAGES.INTRO)} onNext={() => setCurrentPage(PAGES.PERMISSIONS)} />
      case PAGES.PERMISSIONS:
        return (
          <Permissions
            onBack={() => setCurrentPage(PAGES.TIPS)}
            onNext={(blob) => {
              console.log('blob', blob)
              if (blob) {
                handleVideoBlob(blob)
                setCurrentPage(PAGES.REVIEW)
              } else {
                setCurrentPage(PAGES.VIDEO_RECORDING)
              }
            }}
          />
        )
      case PAGES.VIDEO_RECORDING:
        return <VideoRecording onBack={() => setCurrentPage(PAGES.PERMISSIONS)} onComplete={handleVideoBlob} />
      case PAGES.REVIEW:
        return (
          <Review
            videoBlob={recordedVideoBlob}
            onBack={() => setCurrentPage(PAGES.VIDEO_RECORDING)}
            isSubmitting={isSubmitting}
            setIsSubmitting={setIsSubmitting}
            uploadProgress={uploadProgress}
            uploadError={uploadError}
          />
        )
      case PAGES.THANK_YOU:
        return <ThankYou />
      default:
        return <Intro onNext={() => setCurrentPage(PAGES.PERMISSIONS)} />
    }
  }

  return (
    <FormProvider onSubmit={handleSubmit}>
      <AppContainer backgroundColor={formConfig?.backgroundColor || '#F7FAFC'} justifyContent="center">
        {renderCurrentPage()}
      </AppContainer>
    </FormProvider>
  )
}

const App = () => (
  <FormConfigProvider>
    <AppContent />
  </FormConfigProvider>
)

export default App
